import React, { useEffect, useMemo, useState } from 'react';
import { FedexPickupAvailabilityOutDTO, FedexPickupDateOptionOutDTO } from '@api/ringil-api.v2';
import { Field } from '@components/framework/formfield/Field.tsx';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { dateToTimeString, timeStringToDate } from '@utils/dateWithTz.tsx';
import dayjs from 'dayjs';

export interface FedexPickupValue {
  // YYYY-MM-DD
  pickupDate?: string;
  // HH:mm, local at the pickup address
  readyTime?: string;
  closeTime?: string;
}

interface ComponentProps {
  // open loading timeslot days, YYYY-MM-DD in route order - the first one is sent to FedEx
  openDays: string[];
  // fetches FedEx availability for the dispatch date (FedEx answers with it + next business days)
  loadAvailability: (dispatchDate: string, onData: (data: FedexPickupAvailabilityOutDTO) => void, onError: () => void) => void;
  // anything else the availability depends on (address, service type) - re-fetches on change
  availabilityKey?: string;
  value: FedexPickupValue;
  onChange: (value: FedexPickupValue) => void;
  // false while required times are missing or break the FedEx cutoff/window rules
  onValidChange?: (valid: boolean) => void;
  timezoneMissing?: boolean;
}

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

const minutesFromHHmm = (value?: string): number | null => {
  if (!value || !HHMM.test(value)) return null;
  const [h, m] = value.split(':').map(Number);
  return h * 60 + m;
};

export const trimToHHmm = (value?: string): string | undefined => {
  if (!value) return undefined;
  return value.length >= 5 ? value.substring(0, 5) : value;
};

/**
 * Local calendar date (YYYY-MM-DD) of a timeslot day. `day` is local midnight sent as UTC
 * (2.11 00:00 CET = "2026-11-01T23:00:00Z"), so cutting its string gives the previous day;
 * `utcDay` is the real local date (what the backend uses).
 */
export const timeslotLocalDay = (day?: { day?: string; utcDay?: string }): string | undefined => {
  if (!day) return undefined;
  if (day.utcDay) return day.utcDay.substring(0, 10);
  return day.day ? dayjs(day.day).format('YYYY-MM-DD') : undefined;
};

// "FedEx Express pickups can be scheduled for the current or next business day" (Pickup API docs)
const fedexLatestPickupDate = (): string => {
  let day = dayjs().add(1, 'day');
  while (day.day() === 0 || day.day() === 6) day = day.add(1, 'day');
  return day.format('YYYY-MM-DD');
};

/**
 * FedEx courier pickup: date (loading timeslot days FedEx also offers), ready and close time.
 * FedEx returns the requested day plus the next business days, each with its own cutoff and
 * minimum ready-to-close window.
 */
const FedexPickupForm: React.FC<ComponentProps> = ({
  openDays,
  loadAvailability,
  availabilityKey,
  value,
  onChange,
  onValidChange,
  timezoneMissing,
}) => {
  const { tr } = useTranslationLgs();
  const [availability, setAvailability] = useState<FedexPickupAvailabilityOutDTO | null>(null);

  const dispatchDate = openDays[0];
  const openDaysKey = openDays.join(',');

  useEffect(() => {
    setAvailability(null);
    if (!dispatchDate) return;
    loadAvailability(
      dispatchDate,
      data => setAvailability(data),
      () => setAvailability(null),
    );
  }, [dispatchDate, availabilityKey]);

  const latestBookable = fedexLatestPickupDate();
  // FedEx lists more days than it books, so a loading day after the next business day can't get a courier yet
  const tooFar = openDays.length > 0 && openDays.every(day => day > latestBookable);

  // only days the loading location is open on, FedEx offers AND FedEx books today
  const offered: FedexPickupDateOptionOutDTO[] = useMemo(
    () =>
      (availability?.options ?? []).filter(o => openDays.includes(o.pickupDate) && o.pickupDate <= latestBookable),
    [availability, openDaysKey, latestBookable],
  );

  const selected = offered.find(o => o.pickupDate === value.pickupDate);

  // keep the value on an offered date, prefilling FedEx's earliest ready / latest close times
  useEffect(() => {
    if (!availability || offered.length === 0 || selected) return;
    const first = offered[0];
    onChange({
      pickupDate: first.pickupDate,
      readyTime: value.readyTime ?? trimToHHmm(first.readyTimeOptions?.[0]),
      closeTime: value.closeTime ?? trimToHHmm(first.latestTimeOptions?.[first.latestTimeOptions.length - 1] ?? first.cutOffTime),
    });
  }, [availability, offered, selected]);

  const cutoffTime = trimToHHmm(selected?.cutOffTime);
  // FedEx location-specific minimum window, never below our conservative 2h floor
  const minWindowMinutes = Math.max(120, selected?.accessTimeMinutes ?? 0);

  const readyError = useMemo(() => {
    if (!value.readyTime) return tr('FedexProductTerms.errorRequired', 'Pickup times are required when ordering a courier');
    if (cutoffTime && HHMM.test(value.readyTime) && minutesFromHHmm(value.readyTime)! > minutesFromHHmm(cutoffTime)!) {
      return tr('FedexProductTerms.errorAfterCutoff', 'Ready time must not be after the FedEx cutoff {{cutoff}}', {
        cutoff: cutoffTime,
      });
    }
    return null;
  }, [value.readyTime, cutoffTime, tr]);

  const closeError = useMemo(() => {
    if (!value.closeTime) return tr('FedexProductTerms.errorRequired', 'Pickup times are required when ordering a courier');
    if (value.readyTime && HHMM.test(value.readyTime) && HHMM.test(value.closeTime)) {
      const gap = minutesFromHHmm(value.closeTime)! - minutesFromHHmm(value.readyTime)!;
      if (gap < minWindowMinutes) {
        return tr('FedexProductTerms.errorMinWindow', 'Close time must be at least {{hours}} h after ready time', {
          hours: Math.round((minWindowMinutes / 60) * 10) / 10,
        });
      }
    }
    return null;
  }, [value.readyTime, value.closeTime, minWindowMinutes, tr]);

  const notOffered = availability != null && offered.length === 0 && !tooFar;

  useEffect(() => {
    onValidChange?.(!!selected && !readyError && !closeError);
  }, [selected, readyError, closeError]);

  return (
    <div className="flex flex-col gap-3">
      {availability && !availability.available && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
          {tr('FedexProductTerms.pickupNotAvailable', 'FedEx courier pickup is not available for this address and date.')}
        </div>
      )}

      {tooFar && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
          {tr(
            'FedexProductTerms.pickupDateTooFar',
            'FedEx books a courier only for today or the next business day (up to {{date}}). Order the courier once the loading day is that close.',
            { date: dayjs(latestBookable).format('DD.MM.YYYY') },
          )}
        </div>
      )}

      {notOffered && availability.available && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
          {tr('FedexProductTerms.pickupDateOfferedOnly', 'FedEx offers pickup on: {{dates}}', {
            dates: (availability.options ?? []).map(o => dayjs(o.pickupDate).format('DD.MM.YYYY')).join(', '),
          })}
        </div>
      )}

      {offered.length > 0 && (
        <Field label={tr('FedexProductTerms.pickupDate', 'Pickup date')} required>
          <Dropdown
            value={value.pickupDate}
            options={offered.map(o => ({ value: o.pickupDate, label: dayjs(o.pickupDate).format('DD.MM.YYYY (dddd)') }))}
            onChange={e => onChange({ ...value, pickupDate: e.value })}
            style={{ width: '100%' }}
          />
        </Field>
      )}

      <Field label={tr('FedexProductTerms.pickupReadyTime', 'Pickup ready time')} required errorMessage={readyError}>
        <Calendar
          timeOnly
          hourFormat="24"
          value={timeStringToDate(value.readyTime)}
          onChange={e => onChange({ ...value, readyTime: dateToTimeString(e.value as Date) ?? undefined })}
          style={{ width: '100%' }}
        />
      </Field>

      <Field label={tr('FedexProductTerms.pickupCloseTime', 'Pickup close time')} required errorMessage={closeError}>
        <Calendar
          timeOnly
          hourFormat="24"
          value={timeStringToDate(value.closeTime)}
          onChange={e => onChange({ ...value, closeTime: dateToTimeString(e.value as Date) ?? undefined })}
          style={{ width: '100%' }}
        />
      </Field>

      {cutoffTime && (
        <div className="text-xs text-content-secondary">
          {tr('FedexProductTerms.windowHint', 'FedEx cutoff: {{cutoff}}, minimum window: {{hours}} h', {
            cutoff: cutoffTime,
            hours: Math.round((minWindowMinutes / 60) * 10) / 10,
          })}
        </div>
      )}

      {timezoneMissing && (
        <div className="text-sm text-red-700">
          {tr('FedexProductTerms.errorMissingTz', 'Pickup location timezone is missing — fill in the loading location first')}
        </div>
      )}
    </div>
  );
};

export default FedexPickupForm;
